// Pure helpers behind the admin pages (no React, unit tested in adminModel.test.js).
import { calendarDay, formatInZone } from '../../design/timeZone';
import { terms as accountTerms } from '../../design/terms';

/* ---------- Names ---------- */

const LEVEL_ABBREVIATION = /\b(div|comm|mgmt|assoc)\.?$/i;
const NAME_ABBREVIATION = /\b(HOA|Mgmt|Maint|Assoc|Dept|Info|Req|Arch|Approx)\b/i;

export const isAbbreviatedLevelName = (name) => LEVEL_ABBREVIATION.test(String(name || '').trim());

/** The abbreviation a taxonomy name uses ("Maint"), or null. */
export const abbreviationIn = (name) => {
  const match = String(name || '').match(NAME_ABBREVIATION);
  return match ? match[0] : null;
};

export const pluralOf = (level) => (level ? level.pluralDisplayName || `${level.displayName}s` : 'Groups');

export const lower = (text) => String(text || '').toLowerCase();

/* ---------- Hierarchy ---------- */

/** Nodes grouped by parent id ('' for top level), each list sorted like the server (sortOrder, then name). */
export function childrenByParent(nodes) {
  const map = new Map();
  nodes.forEach((node) => {
    const key = node.parentId || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(node);
  });
  map.forEach((list) => list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)));
  return map;
}

/**
 * The node Admin › Hierarchy opens on: the first leaf-level node, alphabetically, that has a community a queue or auto
 * attendant maps to directly (on the mockup-parity seed: Brevard, for Viera East Landing Front Desk; Orlando's Lake
 * Nona Estates sorts after it). Without such a mapping, the first leaf-level node alphabetically.
 */
export function defaultHierarchyNode(nodes, leafLevelId, communities, mappings) {
  const leaves = (nodes || []).filter((n) => n.levelId === leafLevelId).sort((a, b) => a.name.localeCompare(b.name));
  const mapped = new Set((mappings || []).filter((m) => m.communityId).map((m) => m.communityId));
  const withMapped = new Set((communities || []).filter((c) => mapped.has(c.id)).map((c) => c.hierarchyNodeId));
  return (leaves.find((n) => withMapped.has(n.id)) || leaves[0] || (nodes || [])[0] || null)?.id || null;
}

/**
 * Users in the mockup's order: staff who have signed in (whole-organization access first, then scoped), then board
 * members, then staff invitations nobody has accepted, then deactivated users; within each, by role, then the
 * least recent sign-in first (never first), then name.
 */
export function sortUsers(users) {
  const roleRank = (u) => {
    const i = ROLES.findIndex((r) => r.id === primaryRole(u.roles));
    return i < 0 ? ROLES.length : i;
  };
  const isBoard = (u) => primaryRole(u.roles) === 'board_member';
  const scoped = (u) => (u.scopedCommunityIds || []).length + (u.scopedHierarchyNodeIds || []).length + (u.scopedQueueIds || []).length > 0 || Boolean(u.ownCallsOnly);
  const group = (u) => (!u.isActive ? 4 : isBoard(u) ? 2 : !u.lastLogin ? 3 : scoped(u) ? 1 : 0);
  const signedIn = (u) => (u.lastLogin ? new Date(u.lastLogin).getTime() : -Infinity);
  return [...(users || [])].sort((a, b) => group(a) - group(b) || roleRank(a) - roleRank(b) || signedIn(a) - signedIn(b) || a.displayName.localeCompare(b.displayName));
}

/** Ids of a node and every node below it. */
export function subtreeIds(nodeId, nodes) {
  const kids = childrenByParent(nodes);
  const out = new Set();
  const walk = (id) => {
    out.add(id);
    (kids.get(id) || []).forEach((child) => walk(child.id));
  };
  walk(nodeId);
  return out;
}

/* ---------- CSV merge report (contracts rule 12: CsvMergeReport) ---------- */

export function summarizeReport(report) {
  const rows = report?.rows || [];
  const created = rows.filter((r) => r.outcome === 'Created');
  const updated = rows.filter((r) => r.outcome === 'Updated');
  const unchanged = rows.filter((r) => r.outcome === 'Unchanged');
  const errorRows = rows.filter((r) => r.outcome === 'Error');
  const errors = (report?.errors || []).map((e) => {
    const row = errorRows.find((r) => r.rowNumber === e.rowNumber);
    return { rowNumber: e.rowNumber, label: row?.label || '', message: e.message || row?.error || '' };
  });
  errorRows.forEach((r) => {
    if (!errors.some((e) => e.rowNumber === r.rowNumber)) errors.push({ rowNumber: r.rowNumber, label: r.label || '', message: r.error || '' });
  });
  errors.sort((a, b) => a.rowNumber - b.rowNumber);
  const fieldChanges = updated.reduce((sum, r) => sum + (r.fieldChanges || []).length, 0);
  return {
    created,
    updated,
    unchanged,
    errors,
    fieldChanges,
    createdCount: report?.created ?? created.length,
    updatedCount: report?.updated ?? updated.length,
    unchangedCount: report?.unchanged ?? unchanged.length,
    changes: (report?.created ?? created.length) + (report?.updated ?? updated.length),
  };
}

/** Totals of several reports (a starter-data preview has three). */
export function combineSummaries(summaries) {
  return summaries.reduce(
    (acc, s) => ({
      createdCount: acc.createdCount + s.createdCount,
      updatedCount: acc.updatedCount + s.updatedCount,
      unchangedCount: acc.unchangedCount + s.unchangedCount,
      errorCount: acc.errorCount + s.errors.length,
      fieldChanges: acc.fieldChanges + s.fieldChanges,
      changes: acc.changes + s.changes,
    }),
    { createdCount: 0, updatedCount: 0, unchangedCount: 0, errorCount: 0, fieldChanges: 0, changes: 0 }
  );
}

export const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many || `${one}s`}`;

export function importButtonLabel(changes, errors) {
  const base = changes === 0 ? 'Import' : `Import ${plural(changes, 'change')}`;
  return errors > 0 ? `${base}, skip ${plural(errors, 'error')}` : base;
}

/** First line of a CSV file: the header cells, for "columns: name, division, …". */
export function csvHeader(text) {
  const firstLine = String(text || '').replace(/^﻿/, '').split(/\r?\n/)[0] || '';
  return firstLine
    .split(',')
    .map((cell) => cell.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}

export function csvRowCount(text) {
  return String(text || '')
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .slice(1)
    .filter((line) => line.trim().length > 0).length;
}

/* ---------- Time ---------- */

const pad = (n) => String(n).padStart(2, '0');

/** "08:00:00" (TimeOnly) -> "8:00 AM". */
export function formatClock(value) {
  if (!value) return '';
  const [h, m] = String(value).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${pad(m || 0)} ${suffix}`;
}

/** "8:00 AM", "8 am", "17:30" -> "08:00:00"; null when it isn't a time. */
export function parseClock(text) {
  const match = String(text || '')
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?\s*m?\.?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const half = match[3] ? match[3].toLowerCase() : null;
  if (minute > 59) return null;
  if (half) {
    if (hour < 1 || hour > 12) return null;
    if (half === 'a') hour = hour === 12 ? 0 : hour;
    else hour = hour === 12 ? 12 : hour + 12;
  } else if (hour > 23) return null;
  return `${pad(hour)}:${pad(minute)}:00`;
}

/** "8 AM", "8:30 PM" (no ":00"), for compact summaries. */
export function shortClock(value) {
  return formatClock(value).replace(':00 ', ' ');
}

export const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAY = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

/** The seven days Monday first, filling missing days as closed. */
export function fullWeek(weeklyHours) {
  return WEEK.map((day) => {
    const found = (weeklyHours || []).find((d) => d.day === day);
    return found ? { ...found, intervals: found.intervals || [] } : { day, isOpen: false, intervals: [] };
  });
}

/** "Mon–Sat 8 AM–8 PM" for a queue's custom hours; days with other hours are listed after a comma. */
export function summarizeWeeklyHours(weeklyHours) {
  const days = fullWeek(weeklyHours).filter((d) => d.isOpen && d.intervals.length);
  if (!days.length) return 'Closed every day';
  const groups = [];
  days.forEach((d) => {
    const span = d.intervals.map((i) => `${shortClock(i.start)}–${shortClock(i.end)}`).join(', ');
    const last = groups[groups.length - 1];
    if (last && last.span === span && WEEK.indexOf(d.day) === WEEK.indexOf(last.to) + 1) last.to = d.day;
    else groups.push({ from: d.day, to: d.day, span });
  });
  return groups.map((g) => `${SHORT_DAY[g.from]}${g.from !== g.to ? `–${SHORT_DAY[g.to]}` : ''} ${g.span}`).join(', ');
}

/** "Today 10:31 AM", "Yesterday", "Oct 13", "Never" in the account time zone. */
export function formatSignIn(value, now = new Date()) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  const day = calendarDay(date);
  const today = calendarDay(now);
  if (day === today) return `Today ${formatInZone(date, { hour: 'numeric', minute: '2-digit' })}`;
  if (day === calendarDay(new Date(now.getTime() - 86400000))) return 'Yesterday';
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return formatInZone(date, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "2026-10-01T..." -> "Oct 1, 2026" in the account time zone. */
export const formatDate = (value) => (value ? formatInZone(new Date(value), { month: 'short', day: 'numeric', year: 'numeric' }) : '');

/** DateOnly "2026-11-26" -> "Nov 26" without a time zone shift. */
/** One chip per holiday row, as the mockup: "Ad-hoc closure" or "Partial day" for those kinds, else "Upcoming" for future dates. */
export function holidayChip(holiday, today) {
  if (holiday.kind === 'AdHocClosure') return ['Ad-hoc closure', 'b-warning'];
  if (holiday.kind === 'PartialDay' || holiday.isClosed === false) return ['Partial day', 'b-warning'];
  return String(holiday.date) >= today ? ['Upcoming', ''] : null;
}

export function formatHolidayDate(value) {
  const [y, m, d] = String(value || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

/* ---------- Users ---------- */

export const ROLES = [
  { id: 'account_admin', label: 'Account administrator', description: 'Everything for {org}, including settings, users, and billing reports.' },
  { id: 'manager', label: 'Manager', description: 'All dashboards, calls, transcripts, insights, and reports within their scope. Can export.' },
  { id: 'supervisor', label: 'Supervisor', description: 'Live wallboard, agents, queues, and calls within their scope. Can listen to recordings.' },
  { id: 'executive', label: 'Executive', description: 'Dashboards, insights, and reports for the whole organization. No transcripts.' },
  { id: 'agent', label: 'Agent', description: 'Settings pages, read only. No dashboards, calls, or reports.' },
  { id: 'board_member', label: 'Board member', description: 'Overview, insights, and saved reports for their {community} only. No calls, transcripts, or exports.' },
  { id: 'super_admin', label: 'Vertolytics super administrator', description: 'Vertolytics support staff only. Support access; turns email delivery on or off.' },
];

export const INVITE_ROLE_ORDER = ['supervisor', 'manager', 'executive', 'agent', 'board_member', 'account_admin'];

export const roleLabel = (id) => (ROLES.find((r) => r.id === id) || { label: id }).label;

/** A role's description with the organization name and the account's word for a community filled in. */
export const roleDescription = (role, org, terms = accountTerms()) => role.description.replace('{org}', org).replace('{community}', terms.communityLower);

/** The role shown for a user: the most privileged one they hold. */
export const primaryRole = (roles) => ROLES.map((r) => r.id).find((id) => (roles || []).includes(id)) || (roles || [])[0] || '';

/**
 * What a user can see, as the mockup's "Can see" cell: { icon, text }.
 * lookups: { nodes: Map(id -> { name, levelName }), communities: Map(id -> name), queues: Map(id -> name) }
 */
export function describeScope(user, lookups, terms = accountTerms()) {
  const nodeIds = user.scopedHierarchyNodeIds || [];
  const communityIds = user.scopedCommunityIds || [];
  const queueIds = user.scopedQueueIds || [];
  const parts = [];
  if (user.ownCallsOnly) return { icon: 'user', text: 'Own calls' };
  const total = nodeIds.length + communityIds.length + queueIds.length;
  if (total === 0) return { icon: 'building', text: 'Whole organization' };
  if (total > 1) {
    if (communityIds.length === total) return { icon: 'home', text: `${communityIds.length} ${terms.communitiesLower}` };
    if (queueIds.length === total) return { icon: 'layers', text: `${queueIds.length} queues` };
    if (nodeIds.length === total) {
      const levelName = lookups.nodes.get(nodeIds[0])?.levelName;
      return { icon: 'sitemap', text: `${nodeIds.length} ${levelName ? `${lower(levelName)}s` : 'groups'}` };
    }
    if (nodeIds.length) parts.push(plural(nodeIds.length, 'group'));
    if (communityIds.length) parts.push(plural(communityIds.length, terms.communityLower, terms.communitiesLower));
    if (queueIds.length) parts.push(plural(queueIds.length, 'queue'));
    return { icon: 'sitemap', text: parts.join(', ') };
  }
  if (nodeIds.length) {
    const node = lookups.nodes.get(nodeIds[0]);
    return { icon: 'sitemap', text: node ? `${node.name} ${lower(node.levelName || 'group')}` : 'A removed group' };
  }
  if (queueIds.length) {
    const name = lookups.queues.get(queueIds[0]);
    return { icon: 'layers', text: name ? `${name} queue` : 'A queue' };
  }
  return { icon: 'home', text: lookups.communities.get(communityIds[0]) || `A removed ${terms.communityLower}` };
}

/** Splits "a@x.com, b@y.org\nc@z.com" into trimmed, de-duplicated addresses. */
export function parseEmails(text) {
  const seen = new Set();
  return String(text || '')
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter((e) => e && !seen.has(e.toLowerCase()) && seen.add(e.toLowerCase()));
}

export const isEmail = (text) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);

/** "dana.whitaker@x.com" -> "Dana Whitaker". */
export function nameFromEmail(email) {
  const local = String(email || '').split('@')[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/* ---------- Taxonomy ---------- */

export const SEVERITIES = [
  { value: 'Immediate', label: 'Immediate' },
  { value: 'Within48Hours', label: 'Within 48 Hours' },
  { value: 'Standard', label: 'Standard' },
  { value: 'Informational', label: 'Informational' },
];

export const versionLabel = (version) => (version ? version.versionLabel || `v1.${version.versionNumber}` : '');

/**
 * Differences between a draft and the version it was based on, by stable ids:
 * { added: [category], removed: [category], edited: [category], addedSubs: [{ category, sub }], removedSubs: [...], renamed: n }
 */
export function diffTaxonomy(draft, base) {
  const result = { added: [], removed: [], edited: [], addedSubs: [], removedSubs: [], renamed: 0 };
  if (!draft) return result;
  const before = new Map((base?.categories || []).map((c) => [c.id, c]));
  const after = new Map((draft.categories || []).map((c) => [c.id, c]));
  after.forEach((category, id) => {
    const old = before.get(id);
    if (!old) {
      result.added.push(category);
      return;
    }
    const oldSubs = new Map((old.subcategories || []).map((s) => [s.id, s]));
    const newSubs = new Map((category.subcategories || []).map((s) => [s.id, s]));
    newSubs.forEach((sub, subId) => {
      if (!oldSubs.has(subId)) result.addedSubs.push({ category, sub });
      else if (oldSubs.get(subId).name !== sub.name) result.renamed += 1;
    });
    oldSubs.forEach((sub, subId) => {
      if (!newSubs.has(subId)) result.removedSubs.push({ category, sub });
    });
    if (old.name !== category.name) result.renamed += 1;
    const changed =
      old.name !== category.name ||
      old.description !== category.description ||
      old.defaultSeverity !== category.defaultSeverity ||
      JSON.stringify(old.examplePhrases || []) !== JSON.stringify(category.examplePhrases || []) ||
      JSON.stringify((old.subcategories || []).map((s) => [s.id, s.name])) !== JSON.stringify((category.subcategories || []).map((s) => [s.id, s.name]));
    if (changed) result.edited.push(category);
  });
  before.forEach((category, id) => {
    if (!after.has(id)) result.removed.push(category);
  });
  return result;
}

/** "definition and example phrases": what an edit changed on a category, besides added or removed subcategories. */
export function describeCategoryEdit(before, after) {
  if (!before || !after) return '';
  const parts = [];
  if (before.name !== after.name) parts.push('name');
  if ((before.description || '') !== (after.description || '')) parts.push('definition');
  if (before.defaultSeverity !== after.defaultSeverity) parts.push('default severity');
  if (JSON.stringify(before.examplePhrases || []) !== JSON.stringify(after.examplePhrases || [])) parts.push('example phrases');
  const oldNames = new Map((before.subcategories || []).map((s) => [s.id, s.name]));
  if ((after.subcategories || []).some((s) => oldNames.has(s.id) && oldNames.get(s.id) !== s.name)) parts.push('subcategory names');
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function describeDiff(diff) {
  const parts = [];
  if (diff.added.length) parts.push(`${plural(diff.added.length, 'category', 'categories')} added`);
  if (diff.addedSubs.length) parts.push(`${plural(diff.addedSubs.length, 'subcategory', 'subcategories')} added`);
  if (diff.removed.length) parts.push(`${plural(diff.removed.length, 'category', 'categories')} removed`);
  if (diff.removedSubs.length) parts.push(`${plural(diff.removedSubs.length, 'subcategory', 'subcategories')} removed`);
  if (diff.renamed) parts.push(`${diff.renamed} renamed`);
  if (!parts.length && diff.edited.length) parts.push(`${plural(diff.edited.length, 'category', 'categories')} edited`);
  return parts.length ? parts.join(', ') : 'no changes yet';
}

/* ---------- AI settings ---------- */

export const RETENTION_OPTIONS = [
  { days: 365, label: '12 months' },
  { days: 180, label: '6 months' },
  { days: 730, label: '24 months' },
  { days: 1095, label: '36 months' },
];

export const retentionLabel = (days) => (RETENTION_OPTIONS.find((o) => o.days === days) || { label: `${days} days` }).label;

/* ---------- AI providers ---------- */

const HEALTH_TONE = {
  Healthy: ['b-good', 'check'],
  Connected: ['b-good', 'check'],
  Partial: ['b-warning', 'alert'],
  NotReachable: ['b-critical', 'alert'],
};

/** The status chip for a provider from GET settings/ai/status (null while unknown). */
export function statusBadge(status) {
  if (!status || !status.label) return null;
  const [tone, icon] = HEALTH_TONE[status.health] || [];
  return [status.label, tone, icon];
}

/** The measured provider with the best classification accuracy (its card names the labelled set), or null. */
export function mostAccurateProvider(statuses) {
  let best = null;
  (statuses || []).forEach((s) => {
    if (s.accuracy && (!best || s.accuracy.classificationPercent > best.accuracy.classificationPercent)) best = s;
  });
  return best ? best.provider : null;
}

/** "Highest accuracy on Contoso's 50-call labelled set (classification 94%, entities 91%)." */
export const accuracySentence = (org, accuracy) =>
  `Highest accuracy on ${org}'s ${accuracy.caseCount}-call labelled set (classification ${accuracy.classificationPercent}%, entities ${accuracy.entityPercent}%).`;

/** Status chip first, then the card's own chips, then the accuracy chip (none for the most accurate provider, whose description carries it). */
export function providerBadges(baseBadges, status, { showAccuracy = false, best = false } = {}) {
  const badges = [];
  const chip = statusBadge(status);
  if (chip) badges.push(chip);
  badges.push(...baseBadges);
  if (showAccuracy && !best) badges.push([status?.accuracy ? `Accuracy ${status.accuracy.classificationPercent}%` : 'Not measured yet']);
  return badges;
}

/* ---------- Communities ---------- */

/** Rows grouped by their node's order, busiest community first within each (calls in the last 30 days), then by name. */
export function communityRows(communities, nodes, stats) {
  const order = new Map((nodes || []).map((n) => [n.id, n.sortOrder ?? 0]));
  const calls = new Map((stats || []).map((st) => [st.communityId, st.callCount || 0]));
  const rank = (c) => (order.has(c.hierarchyNodeId) ? order.get(c.hierarchyNodeId) : Number.MAX_SAFE_INTEGER);
  return [...(communities || [])].sort((a, b) => rank(a) - rank(b) || (calls.get(b.id) || 0) - (calls.get(a.id) || 0) || a.name.localeCompare(b.name));
}

/**
 * "Lake Nona Estates Direct Line, Concierge queue": the community's own auto attendants, then its queues; after the
 * first, a name that starts with the community's name drops it, and such a shortened queue reads "… queue". Empty when none.
 */
export function routedByText(community, entities) {
  const direct = (entities || [])
    .filter((e) => e.mapping && e.mapping.communityId === community.id)
    .sort((a, b) => (a.routingEntityType === b.routingEntityType ? 0 : a.routingEntityType === 'AutoAttendant' ? -1 : 1) || a.routingEntityName.localeCompare(b.routingEntityName));
  const prefix = `${community.name} `;
  return direct
    .map((e, i) => {
      const shortened = i > 0 && e.routingEntityName.startsWith(prefix) && e.routingEntityName.length > prefix.length;
      const name = shortened ? e.routingEntityName.slice(prefix.length) : e.routingEntityName;
      return shortened && e.routingEntityType === 'CallQueue' && !/queue$/i.test(name) ? `${name} queue` : name;
    })
    .join(', ');
}

/* ---------- Taxonomy re-analysis ---------- */

const PROVIDER_NAMES = { Claude: 'Claude', AzureOpenAi: 'Azure OpenAI', Local: 'the local model', Synthetic: 'the synthetic demo analyzer' };

const aboutCount = (n) => (n >= 1000 ? Number(n.toPrecision(2)) : n).toLocaleString('en-US');

const roughDuration = (minutes) => {
  if (minutes < 60) return `roughly ${plural(Math.max(1, minutes), 'minute')}`;
  return `roughly ${plural(Math.round(minutes / 60), 'hour')}`;
};

/** "Re-analyze calls from the last 30 days (about 5,300 calls, roughly 3 hours)" */
export function reanalysisLabel(estimate) {
  const base = `Re-analyze calls from the last ${estimate?.days || 30} days`;
  if (!estimate) return base;
  if (!estimate.callCount) return `${base} (no calls with transcripts)`;
  return `${base} (about ${aboutCount(estimate.callCount)} ${estimate.callCount === 1 ? 'call' : 'calls'}, ${roughDuration(estimate.estimatedMinutes)})`;
}

/** "Re-analysis uses Claude and costs about $0.016 per call. Dashboards update as calls finish." */
export function reanalysisNote(estimate) {
  if (!estimate) return 'Dashboards update as calls finish.';
  const cost = estimate.costPerCallUsd > 0 ? ` and costs about $${Number(estimate.costPerCallUsd).toString()} per call` : '';
  return `Re-analysis uses ${PROVIDER_NAMES[estimate.provider] || estimate.provider}${cost}. Dashboards update as calls finish.`;
}

/* ---------- CSV import ---------- */

/**
 * What each importable file contains; `names` labels the preview tables. Report values are keyed by the column the
 * template names, so the community column is keyed by the account's term.
 */
export function importEntities(levelName, levelPlural, terms = accountTerms()) {
  return {
    communities: {
      label: terms.communities,
      noun: terms.communityLower,
      nouns: terms.communitiesLower,
      rowHeader: terms.community,
      matching: `Vertolytics matches each row to an existing ${terms.communityLower} by name or alias. Rows that match are updated; rows that don't are created. ${terms.communities} missing from the file are left alone.`,
      createdColumns: [
        { key: 'Name', header: 'Name', title: true },
        { key: 'Hierarchy Node', header: levelName },
        { key: 'Homes', header: 'Homes', numeric: true },
        { key: 'Aliases', header: 'Aliases', tags: true },
      ],
    },
    'hierarchy-nodes': {
      label: levelPlural,
      noun: lower(levelName),
      nouns: lower(levelPlural),
      rowHeader: levelName,
      matching: `Vertolytics matches each row to an existing ${lower(levelName)} by code, name, or alias at the same level. Rows that match are updated; rows that don't are created. ${levelPlural} missing from the file are left alone.`,
      createdColumns: [
        { key: 'Name', header: 'Name', title: true },
        { key: 'Level', header: 'Level' },
        { key: 'Parent', header: 'Parent' },
        { key: 'Aliases', header: 'Aliases', tags: true },
      ],
    },
    mappings: {
      label: 'Queue and auto attendant mappings',
      noun: 'mapping',
      nouns: 'mappings',
      rowHeader: 'Queue or auto attendant',
      matching: 'Vertolytics matches each row to an existing mapping by type and resource account id. Rows that match are updated; rows that don\'t are created. Mappings missing from the file are left alone.',
      createdColumns: [
        { key: 'Routing Entity Name', header: 'Name', title: true },
        { key: 'Routing Entity Type', header: 'Type' },
        { key: terms.community, header: terms.community, fallbackKey: 'Hierarchy Node' },
        { key: 'Function', header: 'Function' },
      ],
    },
    directory: {
      label: 'Caller directory',
      noun: 'directory entry',
      nouns: 'directory entries',
      rowHeader: 'Person',
      matching: `Vertolytics matches each row to an existing entry by external id, or by ${terms.communityLower}, full name, and unit or lot. Rows that match are updated; rows that don't are created. Entries missing from the file are left alone.`,
      createdColumns: [
        { key: 'Full Name', header: 'Name', title: true },
        { key: 'Entry Type', header: 'Type' },
        { key: terms.community, header: terms.community },
        { key: 'Phone Numbers', header: 'Phone numbers' },
      ],
    },
  };
}
